import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { Period } from '../utils/periods';
import type { Org } from '../api/types';

export type { Org };
export const ORG_STORAGE_KEY = 'su_dashboard_org';

export type StoreTypeFilter = 'all' | 'PTL' | 'LTL' | 'NEW_STORE';
export type RegionMode = 'all' | 'state';
export type SortKey = 'sales' | 'yoy';
export type SortOrder = 'desc' | 'asc';

export interface SuFiltersState {
  org: Org;
  period: Period;
  storeType: StoreTypeFilter;
  regions: string[];
  cities: string[];
  regionMode: RegionMode;
  sort: { key: SortKey; order: SortOrder };
}

export const initialFiltersState: SuFiltersState = {
  org: 'SU',
  period: 'today',
  storeType: 'all',
  regions: [],
  cities: [],
  regionMode: 'all',
  sort: { key: 'sales', order: 'desc' },
};

const filtersSlice = createSlice({
  name: 'suFilters',
  initialState: initialFiltersState,
  reducers: {
    setPeriod(state, action: PayloadAction<Period>) {
      // WTD has no tab of its own any more: it is shown together with MTD.
      state.period = action.payload === 'wtd' ? 'mtd' : action.payload;
    },
    setStoreType(state, action: PayloadAction<StoreTypeFilter>) {
      state.storeType = action.payload;
    },
    setRegions(state, action: PayloadAction<string[]>) {
      state.regions = action.payload;
    },
    setCities(state, action: PayloadAction<string[]>) {
      state.cities = action.payload;
    },
    setRegionMode(state, action: PayloadAction<RegionMode>) {
      state.regionMode = action.payload;
    },
    setSort(state, action: PayloadAction<{ key: SortKey; order: SortOrder }>) {
      state.sort = action.payload;
    },
    /** Switches org and clears filters that only make sense within one org. */
    setOrg(state, action: PayloadAction<Org>) {
      if (state.org === action.payload) return;
      state.org = action.payload;
      state.regions = [];
      state.cities = [];
      state.storeType = 'all';
    },
    /**
     * Seeds org from the user's org: SU -> SU, ETH -> ETH, ALL -> the stored
     * choice (read by the caller from localStorage) or SU.
     */
    initOrg(state, action: PayloadAction<{ userOrg?: string; stored?: string | null }>) {
      const userOrg = (action.payload.userOrg || 'ALL').toString().trim().toUpperCase();
      const stored = action.payload.stored === 'ETH' || action.payload.stored === 'SU' ? action.payload.stored : null;
      const next: Org = userOrg === 'ETH' ? 'ETH' : userOrg === 'SU' ? 'SU' : stored ?? 'SU';
      if (state.org !== next) {
        state.org = next;
        state.regions = [];
        state.cities = [];
        state.storeType = 'all';
      }
    },
    resetFilters(state) {
      return { ...initialFiltersState, org: state.org };
    },
  },
});

export const {
  setPeriod,
  setStoreType,
  setRegions,
  setCities,
  setRegionMode,
  setSort,
  setOrg,
  initOrg,
  resetFilters,
} = filtersSlice.actions;

type WithFilters = { suFilters: SuFiltersState; authReducer?: { user?: { org?: string } | null } };

export const selectFilters = (s: WithFilters) => s.suFilters;
export const selectPeriod = (s: WithFilters) => s.suFilters.period;
export const selectStoreType = (s: WithFilters) => s.suFilters.storeType;
export const selectRegions = (s: WithFilters) => s.suFilters.regions;
export const selectCities = (s: WithFilters) => s.suFilters.cities;
export const selectRegionMode = (s: WithFilters) => s.suFilters.regionMode;
export const selectSort = (s: WithFilters) => s.suFilters.sort;

export default filtersSlice.reducer;

export const selectUserOrg = (s: WithFilters): 'SU' | 'ETH' | 'ALL' => {
  const v = (s.authReducer?.user?.org || 'ALL').toString().trim().toUpperCase();
  return v === 'SU' || v === 'ETH' ? v : 'ALL';
};
/** SU-only and ETH-only users are pinned to their org; ALL users use the switchable state. */
export const selectOrg = (s: WithFilters): Org => {
  const u = selectUserOrg(s);
  return u === 'ALL' ? s.suFilters.org : u;
};
